import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import api from "../../api/axiosInstance";
import { useProfile } from "../../providers/ProfileProvider";
import AddCustomerModal from "./AddCustomerModal";
import DateField from "./DateField";
import ServiceTypeDialog from "./ServiceTypeDialog";
import { DELIVERY_MODELS, ENGAGEMENT_DURATIONS, FIXED_TERM, STATUSES, inServiceTypeOrder } from "./opportunityOptions";

const ACCENT = "#E84610";
const DARK = "#483C3B";
const TEXT_SECONDARY = "rgba(0, 0, 0, 0.6)";
const BUTTON_SX = {
  height: 42,
  px: "22px",
  py: "8px",
  borderRadius: "8px",
  fontSize: 15,
  lineHeight: "26px",
  fontWeight: 500,
  letterSpacing: "0.46px",
  textTransform: "uppercase",
  boxSizing: "border-box",
};

export const NAME_MAX = 150;
export const DESCRIPTION_MAX = 300;
export const PARTNER_MAX = 120;
const STAFF_UNAVAILABLE = "Staff list unavailable";
const CUSTOMERS_UNAVAILABLE = "Customer list unavailable";
const END_BEFORE_START = "End date must be on or after the start date";
const ADD_CUSTOMER = { _id: "__add__", name: "+ Add New Customer" };
const filterCustomers = createFilterOptions({ stringify: (c) => c.name });

// Dates travel as plain YYYY-MM-DD (stored as UTC midnight), so the picked
// day is the day every screen shows, whatever the browser's time zone.
export const toDateOnly = (date) =>
  `${String(date.getFullYear()).padStart(4, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
export const fromStoredDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};
const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

const personName = (p) => `${p?.firstName || ""} ${p?.lastName || ""}`.trim() || p?.email || "—";

// Server messages start lower-case ("customer not found"); the form shows them as sentences.
const sentence = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

// The server answers 400 with "field: message; field: message" (top-level fields). Map each part
// to its form field; anything else, including lead (no Lead field in 2c), goes to the form alert.
const FIELD_OF = {
  client: "customer",
  name: "name",
  owners: "owners",
  dueDate: "dueDate",
  description: "description",
  engagementDuration: "engagementDuration",
  expectedStartDate: "expectedStartDate",
  expectedEndDate: "expectedEndDate",
  deliveryModel: "deliveryModel",
  serviceTypes: "serviceTypes",
  status: "status",
  partnerName: "partnerName",
};
export const fieldErrorsFrom = (message) => {
  const errors = {};
  String(message || "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const [path, ...rest] = part.split(":");
      const field = FIELD_OF[path.trim()];
      if (field && rest.length) errors[field] = sentence(rest.join(":").trim());
      else errors.form = errors.form ? `${errors.form}; ${part}` : sentence(part);
    });
  return errors;
};

const emptyForm = {
  customer: null,
  name: "",
  owners: [],
  dueDate: null,
  description: "",
  engagementDuration: FIXED_TERM,
  expectedStartDate: null,
  expectedEndDate: null,
  deliveryModel: "",
  serviceTypes: [],
  status: "Business Lead",
  hasPartner: false,
  partnerName: "",
};

// A person may arrive populated or as a bare id; deleted Users arrive as null and are left out.
const idOf = (p) => (p && typeof p === "object" ? p._id : typeof p === "string" ? p : null);

// Records from before 2c have none of the new fields: they open empty, for the manager to fill in.
export const formFrom = (opp) => ({
  customer: opp.client && typeof opp.client === "object" ? opp.client : null,
  name: opp.name || "",
  owners: (opp.owners || []).map(idOf).filter((id) => id && String(id) !== String(idOf(opp.lead))),
  dueDate: fromStoredDate(opp.dueDate),
  description: opp.description || "",
  engagementDuration: opp.engagementDuration || "",
  expectedStartDate: fromStoredDate(opp.expectedStartDate),
  expectedEndDate: opp.engagementDuration === FIXED_TERM ? fromStoredDate(opp.expectedEndDate) : null,
  deliveryModel: opp.deliveryModel || "",
  serviceTypes: inServiceTypeOrder(opp.serviceTypes),
  status: opp.status || "Business Lead",
  hasPartner: Boolean(opp.partnerName),
  partnerName: opp.partnerName || "",
});

export const endBeforeStart = (form) =>
  form.engagementDuration === FIXED_TERM &&
  isValidDate(form.expectedStartDate) &&
  isValidDate(form.expectedEndDate) &&
  form.expectedEndDate < form.expectedStartDate;

// `excluded` is the person not counted as an owner: the creator on Create, the lead on Edit.
export const canSubmit = (form, { excluded, staffError, staffLoading, profileReady = true, saving } = {}) =>
  Boolean(form.customer) &&
  form.name.trim() !== "" &&
  form.owners.some((id) => String(id) !== String(excluded)) &&
  isValidDate(form.dueDate) &&
  ENGAGEMENT_DURATIONS.includes(form.engagementDuration) &&
  isValidDate(form.expectedStartDate) &&
  (form.engagementDuration !== FIXED_TERM || (isValidDate(form.expectedEndDate) && !endBeforeStart(form))) &&
  DELIVERY_MODELS.includes(form.deliveryModel) &&
  form.serviceTypes.length > 0 &&
  (!form.hasPartner || form.partnerName.trim() !== "") &&
  profileReady &&
  !staffError &&
  !staffLoading &&
  !saving;

// The request body. Lead and status are never sent on create (the server sets them);
// the end date is sent only for a fixed term.
export const bodyFrom = (form, { editing, excluded }) => ({
  client: form.customer._id,
  name: form.name.trim(),
  owners: form.owners.filter((id) => String(id) !== String(excluded)),
  dueDate: toDateOnly(form.dueDate),
  description: form.description,
  engagementDuration: form.engagementDuration,
  expectedStartDate: toDateOnly(form.expectedStartDate),
  ...(form.engagementDuration === FIXED_TERM ? { expectedEndDate: toDateOnly(form.expectedEndDate) } : {}),
  deliveryModel: form.deliveryModel,
  serviceTypes: inServiceTypeOrder(form.serviceTypes),
  ...(editing ? { status: form.status } : {}),
  hasPartner: form.hasPartner,
  ...(form.hasPartner ? { partnerName: form.partnerName.trim() } : {}),
});

// Create (opportunity = null) or edit an opportunity.
const OpportunityFormModal = ({ open, opportunity, onClose, onSaved }) => {
  const editing = Boolean(opportunity);
  const profile = useProfile();
  const [form, setForm] = useState(emptyForm);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffError, setStaffError] = useState("");
  const [staffLoading, setStaffLoading] = useState(false);
  const [customersError, setCustomersError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [choosingServiceTypes, setChoosingServiceTypes] = useState(false);

  // Not offered or counted as an owner: the creator (who becomes the lead) on Create, the lead on Edit.
  const excluded = editing ? idOf(opportunity.lead) : profile?._id;

  useEffect(() => {
    if (!open) return;
    setForm(opportunity ? formFrom(opportunity) : emptyForm);
    setErrors({});
    setStaffError("");
    setCustomersError("");
    setStaff([]);
    setStaffLoading(true);
    api
      .get("/clients", { params: { kind: "customer" } })
      .then(({ data }) => setCustomers(data || []))
      .catch(() => {
        setCustomers([]);
        setCustomersError(CUSTOMERS_UNAVAILABLE);
      });
    api
      .get("/opportunities/staff")
      .then(({ data }) => setStaff(data || []))
      .catch(() => setStaffError(STAFF_UNAVAILABLE))
      .finally(() => setStaffLoading(false));
  }, [open, opportunity]);

  const set = (field) => (value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined, form: undefined }));
  };

  // Only a fixed term has an end date: leaving it clears the end (approved, 2c).
  const setEngagementDuration = (value) => {
    setForm((f) => ({ ...f, engagementDuration: value, expectedEndDate: value === FIXED_TERM ? f.expectedEndDate : null }));
    setErrors((e) => ({ ...e, engagementDuration: undefined, expectedEndDate: undefined, form: undefined }));
  };

  // Owners already on an edited opportunity who are no longer staff stay visible, marked.
  // Nobody is marked until the staff list has loaded.
  const people = useMemo(() => {
    const byId = new Map(staff.map((s) => [String(s._id), { ...s, notStaff: false }]));
    if (!staffError && opportunity) {
      (opportunity.owners || [])
        .filter((p) => p && typeof p === "object" && !byId.has(String(p._id)))
        .forEach((p) => byId.set(String(p._id), { ...p, notStaff: !staffLoading }));
    }
    return [...byId.values()];
  }, [staff, staffError, staffLoading, opportunity]);
  const label = (p) => (p.notStaff ? `${personName(p)} (not staff)` : personName(p));
  const ownerOptions = people.filter((p) => String(p._id) !== String(excluded));
  const selectedOwners = form.owners.map((id) => people.find((p) => String(p._id) === String(id))).filter(Boolean);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    const body = bodyFrom(form, { editing, excluded });
    setSaving(true);
    setErrors({});
    try {
      const { data } = editing
        ? await api.put(`/opportunities/${opportunity._id}`, body)
        : await api.post("/opportunities", body);
      onSaved?.(data);
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error || err.response?.data?.message;
      if (status === 400) setErrors(fieldErrorsFrom(message));
      else if (status === 503) setErrors({ form: `${STAFF_UNAVAILABLE}. Try again.` });
      else setErrors({ form: sentence(message) || "Could not save the opportunity" });
    } finally {
      setSaving(false);
    }
  };

  const ready = canSubmit(form, { excluded, staffError, staffLoading, profileReady: editing || Boolean(profile?._id), saving });
  const fixedTerm = form.engagementDuration === FIXED_TERM;
  const endError = errors.expectedEndDate || (endBeforeStart(form) ? END_BEFORE_START : undefined);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="opportunity-form-title"
      PaperProps={{
        sx: {
          width: 632,
          maxWidth: "calc(100% - 32px)",
          m: 2,
          p: 3,
          borderRadius: "8px",
          boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.3), 0px 2px 6px 2px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        },
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 30 }}>
            <Typography id="opportunity-form-title" component="h2" sx={{ fontSize: 18, lineHeight: "30px", fontWeight: 700 }}>
              {editing ? "Edit Opportunity" : "Create New Opportunity"}
            </Typography>
            <IconButton edge="end" size="small" aria-label="Close dialog" onClick={handleClose} sx={{ color: DARK }}>
              <HighlightOffOutlinedIcon />
            </IconButton>
          </Box>
          {editing ? (
            <Typography data-testid="opportunity-id" sx={{ fontSize: 16, lineHeight: "24px", color: TEXT_SECONDARY }}>
              {opportunity.opportunityId}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 16, lineHeight: "24px", fontWeight: 300, color: TEXT_SECONDARY }}>
              Create a new project or delivery opportunity
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Autocomplete
            id="opportunity-customer"
            options={customers}
            value={form.customer}
            onChange={(_, value) => {
              if (value?._id === ADD_CUSTOMER._id) setAddingCustomer(true);
              else set("customer")(value);
            }}
            filterOptions={(options, state) => [...filterCustomers(options, state), ADD_CUSTOMER]}
            getOptionLabel={(c) => c.name}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderOption={(props, c) =>
              c._id === ADD_CUSTOMER._id ? (
                <li {...props} key={c._id}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  Add New Customer
                </li>
              ) : (
                <li {...props} key={c._id}>
                  {c.name}
                </li>
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer"
                required
                placeholder="Select customer"
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.customer || customersError)}
                helperText={errors.customer || customersError}
              />
            )}
          />

          <TextField
            id="opportunity-name"
            label="Opportunity Name"
            required
            placeholder="Enter Name"
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
            inputProps={{ maxLength: NAME_MAX }}
            InputLabelProps={{ shrink: true }}
            error={Boolean(errors.name)}
            helperText={errors.name}
            fullWidth
          />

          {/* Free text until 2c-3 brings the partner picker. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.hasPartner}
                  onChange={(e) => {
                    const hasPartner = e.target.checked;
                    setForm((f) => ({ ...f, hasPartner, partnerName: hasPartner ? f.partnerName : "" }));
                  }}
                  sx={{ "&.Mui-checked": { color: ACCENT } }}
                />
              }
              label="Partner company Involved?"
            />
            {form.hasPartner && (
              <TextField
                id="opportunity-partner"
                label="Partner company name"
                required
                placeholder="Eg. Acme Partners AB"
                value={form.partnerName}
                onChange={(e) => set("partnerName")(e.target.value)}
                inputProps={{ maxLength: PARTNER_MAX }}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.partnerName)}
                helperText={errors.partnerName}
                fullWidth
              />
            )}
          </Box>

          <Autocomplete
            id="opportunity-owners"
            multiple
            disableCloseOnSelect
            options={ownerOptions}
            value={selectedOwners}
            onChange={(_, value) => set("owners")(value.map((p) => p._id))}
            getOptionLabel={label}
            getOptionDisabled={(p) => p.notStaff}
            isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
            disabled={Boolean(staffError) || staffLoading}
            renderOption={(props, p, { selected }) => (
              <li {...props} key={p._id}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5, "&.Mui-checked": { color: ACCENT } }} />
                {label(p)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Other Opportunity Owners"
                required
                placeholder={form.owners.length ? "" : "Select owner"}
                InputLabelProps={{ shrink: true }}
                error={Boolean(staffError || errors.owners)}
                helperText={staffError || errors.owners}
              />
            )}
          />

          <DateField
            id="opportunity-due-date"
            label="Opportunity Due Date"
            required
            value={form.dueDate}
            onChange={set("dueDate")}
            error={errors.dueDate}
          />

          <Box>
            <TextField
              id="opportunity-description"
              label="Description"
              placeholder="Brief description of the opportunity"
              value={form.description}
              onChange={(e) => set("description")(e.target.value.slice(0, DESCRIPTION_MAX))}
              inputProps={{ maxLength: DESCRIPTION_MAX, "aria-describedby": "description-counter" }}
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.description)}
              helperText={errors.description}
              multiline
              minRows={3}
              fullWidth
            />
            <Typography id="description-counter" data-testid="description-counter" sx={{ fontSize: 12, mt: 0.5, color: TEXT_SECONDARY }}>
              {form.description.length}/{DESCRIPTION_MAX} characters
            </Typography>
          </Box>

          <FormControl error={Boolean(errors.engagementDuration)}>
            <FormLabel id="opportunity-engagement-duration-label" sx={{ fontSize: 16, color: "text.primary", "&.Mui-focused": { color: "text.primary" } }}>
              Engagement Duration
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="opportunity-engagement-duration-label"
              value={form.engagementDuration}
              onChange={(e) => setEngagementDuration(e.target.value)}
            >
              {ENGAGEMENT_DURATIONS.map((d) => (
                <FormControlLabel key={d} value={d} label={d} control={<Radio sx={{ "&.Mui-checked": { color: ACCENT } }} />} />
              ))}
            </RadioGroup>
            {errors.engagementDuration && <FormHelperText>{errors.engagementDuration}</FormHelperText>}
          </FormControl>

          {form.engagementDuration && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: fixedTerm ? "1fr 1fr" : "1fr" }, gap: 2 }}>
              <DateField
                id="opportunity-start-date"
                label="Expected Start Date"
                required
                value={form.expectedStartDate}
                onChange={set("expectedStartDate")}
                error={errors.expectedStartDate}
              />
              {fixedTerm && (
                <DateField
                  id="opportunity-end-date"
                  label="Expected End Date"
                  required
                  value={form.expectedEndDate}
                  onChange={set("expectedEndDate")}
                  error={endError}
                />
              )}
            </Box>
          )}

          <FormControl required error={Boolean(errors.deliveryModel)}>
            <InputLabel id="opportunity-delivery-model-label" shrink>Delivery Model</InputLabel>
            <Select
              labelId="opportunity-delivery-model-label"
              id="opportunity-delivery-model"
              label="Delivery Model"
              notched
              displayEmpty
              value={form.deliveryModel}
              onChange={(e) => set("deliveryModel")(e.target.value)}
              renderValue={(value) => value || <Box component="span" sx={{ color: TEXT_SECONDARY }}>Select delivery model</Box>}
            >
              {DELIVERY_MODELS.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
            {errors.deliveryModel && <FormHelperText>{errors.deliveryModel}</FormHelperText>}
          </FormControl>

          <TextField
            id="opportunity-service-types"
            label="Service Type"
            required
            placeholder="Select service type"
            value={form.serviceTypes.join(", ")}
            onClick={() => setChoosingServiceTypes(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setChoosingServiceTypes(true);
              }
            }}
            InputProps={{ readOnly: true, sx: { cursor: "pointer", "& input": { cursor: "pointer", textOverflow: "ellipsis" } } }}
            inputProps={{ title: form.serviceTypes.join(", "), "aria-haspopup": "dialog" }}
            InputLabelProps={{ shrink: true }}
            error={Boolean(errors.serviceTypes)}
            helperText={errors.serviceTypes}
            fullWidth
          />

          {editing && (
            <FormControl error={Boolean(errors.status)}>
              <InputLabel id="opportunity-status-label" shrink>Status</InputLabel>
              <Select
                labelId="opportunity-status-label"
                id="opportunity-status"
                label="Status"
                notched
                value={STATUSES.includes(form.status) ? form.status : ""}
                onChange={(e) => set("status")(e.target.value)}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
              {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
            </FormControl>
          )}

          {errors.form && (
            <Typography role="alert" sx={{ fontSize: 14, color: "error.main" }}>
              {errors.form}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              ...BUTTON_SX,
              color: DARK,
              borderColor: DARK,
              backgroundColor: "#FFFFFF",
              "&:hover": { borderColor: DARK, backgroundColor: "#FFFFFF" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!ready}
            sx={{
              ...BUTTON_SX,
              backgroundColor: ACCENT,
              "&:hover": { backgroundColor: "#C93B0A" },
              "&.Mui-disabled": { backgroundColor: "#E8A090", color: "#FFFFFF" },
            }}
          >
            {editing ? "Save Changes" : "Create Opportunity"}
          </Button>
        </Box>
      </LocalizationProvider>

      <ServiceTypeDialog
        open={choosingServiceTypes}
        value={form.serviceTypes}
        onClose={() => setChoosingServiceTypes(false)}
        onApply={(types) => {
          setChoosingServiceTypes(false);
          set("serviceTypes")(types);
        }}
      />

      <AddCustomerModal
        open={addingCustomer}
        onClose={() => setAddingCustomer(false)}
        onCreated={(customer) => {
          setAddingCustomer(false);
          setCustomers((list) => [...list, customer].sort((a, b) => a.name.localeCompare(b.name)));
          set("customer")(customer);
        }}
      />
    </Dialog>
  );
};

export default OpportunityFormModal;
